import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import { colors } from 'utils'
import Icon, { IconProps, IconOption } from './Icon'

interface CustomButtonProps {
    variant?: 'primary' | 'secondary' | 'success' | 'danger'
    children?: React.ReactNode
    icon?: IconOption
    style?: React.CSSProperties
    iconStyle?: React.CSSProperties
    iconProps?: Partial<IconProps>
    onClick?: (input?: any) => void
    className?: string
    size?: 'sm'
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
    tooltip?: string
}

const S = {
    Button: styled.button<CustomButtonProps>`
        all: unset;
        position: relative;
        display: inline-flex;
        max-width: 220px;
        height: ${({ size }) => (size === 'sm' ? 24 : 32)}px;
        padding: 0 16px;
        margin: 0;
        font-size: 14px;
        line-height: ${({ size }) => (size === 'sm' ? 22 : 30)}px;
        border-radius: 4px;
        color: ${({ variant }) => (variant === 'secondary' ? colors.icon : 'white')};
        background-color: ${({ variant }) => (variant === 'secondary' ? 'white' : colors[variant])};
        border: ${({ variant }) => (variant === 'secondary' && '1px solid #E8E9E9')};
        cursor: pointer;
        transition: 0.2s;
        &:hover {
            filter: grayscale(30%);
            background-color: ${({ variant }) => variant === 'secondary' && colors.gray3}!important;
        }
        ${({ disabled }) => disabled && css`
            opacity: 0.6;
            pointer-events: none;
        `}
    `,
    Text: styled.span<CustomButtonProps>`
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `,
    Tooltip: styled(Tooltip)`
        & .arrow {
            display: none;
        }
        & .tooltip-inner {
            max-width: 250px;
            padding: 8px 10px;
            background: ${colors.dark};
            opacity: 0.95;
            box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2);
            text-align: center;
            font-weight: 300;
            font-size: 14px;
            line-height: 16px;
            border-radius: 5px;
        }
    `
}

const Button = ({
    variant = 'primary',
    children,
    icon,
    style,
    iconStyle,
    iconProps,
    onClick,
    className,
    size,
    disabled,
    type = 'button',
    tooltip,
}: CustomButtonProps) => {
    const renderButton = () => (
        <S.Button
            className={className}
            style={{ ...style }}
            onClick={onClick}
            variant={variant}
            size={size}
            disabled={disabled}
            type={type}
        >
            {icon
            && (
                <Icon
                    icon={icon}
                    size={14}
                    style={{ marginBottom: '3px', marginRight: '3px', ...iconStyle }}
                    color={variant !== 'secondary' ? 'white' : colors.grayTitle}
                    {...iconProps}
                />
            )}
            <S.Text size={size}>{children}</S.Text>
        </S.Button>
    )

    return tooltip
        ? (
            <OverlayTrigger
                placement="top"
                overlay={(
                    tooltip ? (
                        <S.Tooltip>
                            {tooltip}
                        </S.Tooltip>
                    ) : <div />
                )}
            >
                <div>
                    {renderButton()}
                </div>
            </OverlayTrigger>
        ) : (
            renderButton()
        )
}

export default Button
